import React from 'react';
import Axios from 'axios';
import ReactSwitch from 'react-switch';
import { FORM_ERROR } from 'final-form';
import { Field } from 'react-final-form';
import PropTypes from 'prop-types';
import Tooltip from 'common/Tooltip';
import { CheckboxField } from 'common/Forms';
import { renderErrors, SubmitError } from 'common/Forms/Utils';
import SubmitButton from 'common/Forms/SubmitButton';
import showToast from 'common/Toast';
import { teacher_program_classrooms_path } from 'modules/routes';
import useProgramContext from '@/hooks/useProgramContext';
import Text from '../../../ui/Text';
import cn from '../../../../utils/cn';
import Icon from '../../../ui/Icon';
import styles from './Grades/GradesForm.module.scss';

export const SwitchAdapter = ({ ...props }) => (
  <ReactSwitch
    checkedIcon={false}
    handleDiameter={20}
    height={24}
    offColor="#cccccc"
    uncheckedIcon={false}
    width={48}
    onColor="#2A8806"
    {...props}
  />
);

export const InfoTooltip = ({ content }) => (
  <Tooltip content={content} size="medium" theme="white">
    <span>
      <span className="sr-only">More info</span>
      <Icon className="tw-ml-1" icon="InfoAlternate" size={14} />
    </span>
  </Tooltip>
);

InfoTooltip.propTypes = {
  content: PropTypes.string.isRequired
};

export const SelectField = ({
  indented, label, name, onChange, options, rightAligned, sectionHeader, tooltip
}) => (
  <div className={cn(styles.selectContainer, { 'tw-justify-between': rightAligned })}>
    <label className={cn({ [styles.indented]: indented }, 'tw-inline-flex tw-items-center tw-w-1/2')} htmlFor={name}>
      <Text className={sectionHeader} size="sm" variant="label">{label}</Text>
      {tooltip && <InfoTooltip content={tooltip} />}
    </label>

    <Field
      className={styles.select}
      component="select"
      id={name}
      name={name}
      placeholder={null}
      onChange={onChange}
    >
      {options.map(optionData => (
        <option key={optionData.value} value={optionData.value}>{optionData.label}</option>
      ))}
    </Field>
  </div>
);

SelectField.propTypes = {
  indented: PropTypes.number,
  label: PropTypes.node.isRequired,
  name: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        label: PropTypes.string,
        value: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.bool])
      })
    ])
  ).isRequired,
  rightAligned: PropTypes.bool,
  sectionHeader: PropTypes.string,
  tooltip: PropTypes.string
};

export function handleSelect(args, state, utils) {
  let value = args[0].target.value;
  if (value === 'true' || value == 'false') value = JSON.parse(value);

  utils.changeValue(state, state.formState.active, () => value);
}

export const handleCheckbox = (args, state, utils) => {
  const isOn = args[0].target.value === 'true';
  utils.changeValue(state, args[0].target.name, () => !isOn);
};

SelectField.defaultProps = {
  indented: 0,
  rightAligned: false,
  sectionHeader: '',
  tooltip: null
};

export const optionList = (start, end, step, suffix = '') => {
  const optionsArray = [];

  for (let value = start; value <= end; value += step) {
    optionsArray.push({ label: `${value}${suffix}`, value: value });
  }

  return optionsArray;
};

export function formHandleSelect(args, state, utils) {
  const value = args[0] === null ? null : args[0].value;
  utils.changeValue(state, state.formState.active, () => value);
}

export const Header = ({ text, showEditPerStudentLink = false }) => {
  const { programId } = useProgramContext();

  return (
    <div className="tw-mb-5 tw-flex tw-items-center">
      <Text className="tw-mr-5" variant="heading5">{text}</Text>
      {/* <h1 className="tw-mr-5 tw-text-base">{text}</h1> */}
      {showEditPerStudentLink && (
        <Text asChild className="tw-mt-0.5" size="sm">
          <a href={teacher_program_classrooms_path(programId)} rel="noopener noreferrer" target="_blank">
            Edit per individual student
          </a>
        </Text>
      )}
    </div>
  );
};

export const ActionButtonsWithError = ({ handleCancel = null, submitError, submitting }) => (
  <>
    <SubmitError error={submitError} />
    <hr />
    <div className="tw-flex tw-justify-end tw-sticky tw-bottom-0 tw-bg-white tw-pb-3 tw-px-0">
      {handleCancel && (
        <button
          className="btn btn--link-purple"
          type="button"
          onClick={handleCancel}
        >
            Cancel
        </button>
      )}

      <SubmitButton
        submitButtonText="Update"
        submitting={submitting}
      />
    </div>
  </>
);

export const handleSubmit = async ({ values = {}, setSubmitting, stafferPath, onSubmitSuccess }) => {
  setSubmitting(true);

  return Axios
    .put(stafferPath, values)
    .then((response) => {
      showToast('Save successful');
      setSubmitting(false);
      onSubmitSuccess({ settings: response.data.data.settings });
    })
    .catch((error) => {
      setSubmitting(false);
      showToast('Save failed', { msgType: 'error' });
      return { [FORM_ERROR]: renderErrors(error.response.data.errors) };
    });
};

export const CheckboxOrRadioGroup = ({ form, values, label, name, text = null, negativeSetting = false }) => (
  <div className="tw-mb-6">
    <CheckboxField
      useTextComponent
      checked={negativeSetting ? !values[name] : values[name]}
      fieldClass="tw-flex tw-justify-end"
      label={label}
      labelClass="tw-w-full tw-font-bold tw-mb-3"
      name={name}
      rowClass="tw-flex tw-justify-between"
      onChange={form.mutators.handleCheckbox}
    />
    <Text size="sm">
      {text}
    </Text>
  </div>
);
